import { APP_CONFIG, PRIMARY_BASE, FALLBACK_BASE } from "./config";
import axios from "axios";

let dynamicToken        = null;
let dynamicTokenExpiry  = 0;
let dynamicTokenPromise = null;
export let cachedTenantConfig = null;

const hostUrl = APP_CONFIG.HOST_URL;

// -- Restore token + tenant config from localStorage on startup --
try {
  const t = JSON.parse(localStorage.getItem('_dyn_tkn') || 'null');
  if (t && t.expiry > Date.now() + 30000) {
    dynamicToken       = t.token;
    dynamicTokenExpiry = t.expiry;
  }
} catch { /* ignore */ }
try {
  const c = JSON.parse(localStorage.getItem('_tenant_cfg') || 'null');
  if (c && c.expiry > Date.now()) cachedTenantConfig = c.config;
} catch { /* ignore */ }

const isValid = (expiry) => expiry > 0 && Date.now() < expiry - 30000;

// ─────────────────────────────────────────────
// GET DYNAMIC TOKEN — single server call
// ─────────────────────────────────────────────
const getDynamicToken = async (forceRefresh = false) => {
  if (!forceRefresh && dynamicToken && isValid(dynamicTokenExpiry)) {
    return dynamicToken;
  }

  dynamicToken       = null;
  dynamicTokenExpiry = 0;

  if (!dynamicTokenPromise) {
    dynamicTokenPromise = (async () => {
      const tokenUrls = [APP_CONFIG.GET_TOKEN_URL, `${FALLBACK_BASE}/get-token.php`];
      let res, lastTokenError;
      for (const tokenUrl of tokenUrls) {
        try {
          res = await axios.post(
            tokenUrl,
            { HOST_URL: hostUrl, PROJECT_TYPE: APP_CONFIG.PROJECT_TYPE },
            { headers: { 'Content-Type': 'application/json' } }
          );
          break;
        } catch (err) {
          lastTokenError = err;
        }
      }
      if (!res) throw lastTokenError;

      const data = res.data;
      if (!data?.access_token) throw new Error('TOKEN_MISSING');

      dynamicToken       = data.access_token;
      dynamicTokenExpiry = Date.now() + (data.expires_in ?? 3600) * 1000;

      // Cache token
      try {
        localStorage.setItem('_dyn_tkn', JSON.stringify({ token: dynamicToken, expiry: dynamicTokenExpiry }));
      } catch { /* ignore */ }

      // Cache tenant config if returned alongside token
      if (data.tenant_config) {
        cachedTenantConfig = data.tenant_config;
        try {
          localStorage.setItem('_tenant_cfg', JSON.stringify({
            config: data.tenant_config,
            expiry: Date.now() + 10 * 60 * 1000, // 10 min
          }));
        } catch { /* ignore */ }
      }

      return dynamicToken;
    })().finally(() => { dynamicTokenPromise = null; });
  }

  return dynamicTokenPromise;
};

// ─────────────────────────────────────────────
// PUBLIC
// ─────────────────────────────────────────────
export const getOAuthToken = () => getDynamicToken();

export const authFetch = async (input, init = {}) => {
  const token = await getDynamicToken();

  const makeHeaders = (tkn) => ({ ...init.headers, Authorization: `Bearer ${tkn}` });

  let res;
  try {
    res = await fetch(input, { ...init, headers: makeHeaders(token) });
  } catch {
    // Primary domain unreachable — try fallback domain
    const fallbackInput = input.startsWith(PRIMARY_BASE)
      ? input.replace(PRIMARY_BASE, FALLBACK_BASE)
      : input;
    res = await fetch(fallbackInput, { ...init, headers: makeHeaders(token) });
  }

  if (res.status === 401) {
    const newToken = await getDynamicToken(true);
    res = await fetch(input, { ...init, headers: makeHeaders(newToken) });
  }

  return res;
};

export const clearAuth = () => {
  dynamicToken       = null;
  dynamicTokenExpiry = 0;
  cachedTenantConfig = null;
  try { localStorage.removeItem('_dyn_tkn'); } catch { /* ignore */ }
  try { localStorage.removeItem('_tenant_cfg'); } catch { /* ignore */ }
};
